// ============================================================================
// FILE: configurationValidation.js
// Component configuration validation utilities (works with useComponentLibrary hook)
// ============================================================================
// client/src/validations/configurationValidation.js

import {
  parseValidationResponse,
  createErrorResponse,
  createSuccessResponse,
  createWarningResponse,
  handleApiError,
  mergeValidationResponses
} from './validationHelpers';

// ============================================================================
// USAGE NOTE
// ============================================================================
// Use in ComponentConfigurationModal.jsx:
//
// import useComponentLibrary from '../../hooks/useComponentLibrary';
// import { parseConfigValidation, validateAllComponentConfigs } from '../../validations/configurationValidation';
//
// const { validateComponentConfiguration } = useComponentLibrary();
//
// const handleSave = async () => {
//   // Validate single component (backend)
//   const result = await validateComponentConfiguration(component.componentId, component);
//   const parsed = parseConfigValidation(result);
//   
//   if (!parsed.valid) {
//     setValidationResults(parsed);
//     setShowValidationPanel(true);
//     return;
//   }
//   
//   // Save component
//   onSave(component);
// };
//
// OR validate all components in circuit:
//
// const validateAll = () => {
//   const result = validateAllComponentConfigs(circuit, validateComponentConfiguration);
//   setValidationResults(result);
// };
// ============================================================================

/**
 * Parse component configuration validation response from backend
 * Use this after calling useComponentLibrary().validateComponentConfiguration()
 * 
 * @param {Object} backendResponse - Response from validateComponentConfiguration hook
 * @returns {ValidationResponse} Standardized validation response
 */
export function parseConfigValidation(backendResponse) {
  try {
    console.log('🔍 Parsing configuration validation response');

    if (!backendResponse) {
      return createErrorResponse('No validation response received');
    }

    // Parse using standard helper
    const parsed = parseValidationResponse(backendResponse);

    console.log('✅ Configuration validation parsed:', {
      valid: parsed.valid,
      errors: parsed.errors.length,
      warnings: parsed.warnings.length
    });

    return parsed;

  } catch (error) {
    console.error('❌ Error parsing configuration validation:', error);
    return handleApiError(error, 'Configuration validation parsing');
  }
}

// ============================================================================
// VALIDATE ALL COMPONENTS IN CIRCUIT
// ============================================================================

/**
 * Validate all components in a circuit that need configuration
 * This calls the backend validation for each component
 * 
 * @param {Object} circuit - Circuit object with components
 * @param {Function} validateHookFn - The validateComponentConfiguration function from hook
 * @returns {Promise<ValidationResponse>} Combined validation results
 */
export async function validateAllComponentConfigs(circuit, validateHookFn) {
  try {
    console.log('🔍 Validating all component configurations in circuit');

    if (!circuit || !circuit.components) {
      return createErrorResponse('No circuit or components provided');
    }

    if (!validateHookFn) {
      return createErrorResponse('Validation hook function not provided');
    }

    // Filter components that need configuration
    const componentsToValidate = circuit.components.filter(
      comp => comp.needsConfiguration === true
    );

    if (componentsToValidate.length === 0) {
      return createSuccessResponse([]);
    }

    console.log(`📋 Found ${componentsToValidate.length} components to validate`);

    // Validate each component
    const validationPromises = componentsToValidate.map(async (component) => {
      try {
        const response = await validateHookFn(component.componentId, component);
        const parsed = parseConfigValidation(response);
        
        // Add component info to each error/warning
        parsed.errors = parsed.errors.map(err => ({
          ...err,
          component: component.instanceId,
          componentName: component.componentName || component.name
        }));
        
        parsed.warnings = parsed.warnings.map(warn => ({
          ...warn,
          component: component.instanceId,
          componentName: component.componentName || component.name
        }));
        
        return parsed;
      } catch (error) {
        console.error(`❌ Error validating ${component.componentName}:`, error);
        return createErrorResponse(`Failed to validate ${component.componentName}: ${error.message}`);
      }
    });

    // Wait for all validations to complete
    const results = await Promise.all(validationPromises);

    // Merge all results
    const merged = mergeValidationResponses(results);

    console.log('✅ All component configurations validated:', {
      totalComponents: componentsToValidate.length,
      valid: merged.valid,
      totalErrors: merged.errors.length,
      totalWarnings: merged.warnings.length
    });

    return merged;

  } catch (error) {
    console.error('❌ Error validating all configurations:', error);
    return handleApiError(error, 'All components configuration validation');
  }
}

// ============================================================================
// FILTER BY PROTOCOL TYPE
// ============================================================================

/**
 * Filter WiFi configuration validation results
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterWiFiValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const wifiErrors = validationResult.errors.filter(error =>
    error.protocol === 'wifi' ||
    error.message?.toLowerCase().includes('wifi') ||
    error.message?.toLowerCase().includes('ssid') ||
    error.message?.toLowerCase().includes('dhcp')
  );

  const wifiWarnings = validationResult.warnings.filter(warning =>
    warning.protocol === 'wifi' ||
    warning.message?.toLowerCase().includes('wifi')
  );

  return {
    success: validationResult.success,
    valid: wifiErrors.length === 0,
    errors: wifiErrors,
    warnings: wifiWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'wifi'
    }
  };
}

/**
 * Filter MQTT configuration validation results
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterMQTTValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const mqttErrors = validationResult.errors.filter(error =>
    error.protocol === 'mqtt' ||
    error.message?.toLowerCase().includes('mqtt') ||
    error.message?.toLowerCase().includes('broker') ||
    error.message?.toLowerCase().includes('topic')
  );

  const mqttWarnings = validationResult.warnings.filter(warning =>
    warning.protocol === 'mqtt' ||
    warning.message?.toLowerCase().includes('mqtt') ||
    warning.message?.toLowerCase().includes('ssl')
  );

  return {
    success: validationResult.success,
    valid: mqttErrors.length === 0,
    errors: mqttErrors,
    warnings: mqttWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'mqtt'
    }
  };
}

/**
 * Filter Bluetooth configuration validation results
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterBluetoothValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const bleErrors = validationResult.errors.filter(error =>
    error.protocol === 'bluetooth' ||
    error.message?.toLowerCase().includes('bluetooth') ||
    error.message?.toLowerCase().includes('ble')
  );

  const bleWarnings = validationResult.warnings.filter(warning =>
    warning.protocol === 'bluetooth' ||
    warning.message?.toLowerCase().includes('bluetooth') ||
    warning.message?.toLowerCase().includes('bonding')
  );

  return {
    success: validationResult.success,
    valid: bleErrors.length === 0,
    errors: bleErrors,
    warnings: bleWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'bluetooth'
    }
  };
}

/**
 * Filter software pin validation results
 * @param {ValidationResponse} validationResult
 * @returns {ValidationResponse}
 */
export function filterSoftwarePinValidation(validationResult) {
  if (!validationResult) return createErrorResponse('No validation result');

  const pinErrors = validationResult.errors.filter(error =>
    error.pin ||
    error.message?.toLowerCase().includes('pin') ||
    error.message?.toLowerCase().includes('topic') ||
    error.message?.toLowerCase().includes('interval')
  );

  const pinWarnings = validationResult.warnings.filter(warning =>
    warning.pin ||
    warning.message?.toLowerCase().includes('pin')
  );

  return {
    success: validationResult.success,
    valid: pinErrors.length === 0,
    errors: pinErrors,
    warnings: pinWarnings,
    metadata: {
      ...validationResult.metadata,
      filteredBy: 'software_pins'
    }
  };
}

// ============================================================================
// FILTER BY COMPONENT
// ============================================================================

/**
 * Get validation results for a specific component
 * @param {ValidationResponse} validationResult
 * @param {string} componentInstanceId - Component instance ID
 * @returns {ValidationResponse}
 */
export function filterByComponent(validationResult, componentInstanceId) {
  if (!validationResult) return createErrorResponse('No validation result');

  const componentErrors = validationResult.errors.filter(error =>
    error.component === componentInstanceId
  );

  const componentWarnings = validationResult.warnings.filter(warning =>
    warning.component === componentInstanceId
  );

  return {
    success: validationResult.success,
    valid: componentErrors.length === 0,
    errors: componentErrors,
    warnings: componentWarnings,
    metadata: {
      ...validationResult.metadata,
      componentId: componentInstanceId
    }
  };
}

/**
 * Group validation results by component
 * @param {ValidationResponse} validationResult
 * @returns {Object} Map of componentId -> validation results
 */
export function groupByComponent(validationResult) {
  if (!validationResult) return {};

  const grouped = {};

  // Group errors by component
  validationResult.errors.forEach(error => {
    const compId = error.component || 'unknown';
    if (!grouped[compId]) {
      grouped[compId] = { errors: [], warnings: [] };
    }
    grouped[compId].errors.push(error);
  });

  // Group warnings by component
  validationResult.warnings.forEach(warning => {
    const compId = warning.component || 'unknown';
    if (!grouped[compId]) {
      grouped[compId] = { errors: [], warnings: [] };
    }
    grouped[compId].warnings.push(warning);
  });

  return grouped;
}

// ============================================================================
// COMPREHENSIVE CONFIGURATION VALIDATION
// ============================================================================

/**
 * Parse and categorize all configuration validation results
 * 
 * @param {Object} validationResponse - Response from validateComponentConfiguration
 * @returns {Object} Categorized validation results
 */
export function parseComprehensiveConfigValidation(validationResponse) {
  try {
    // Parse main validation
    const mainResult = parseConfigValidation(validationResponse);

    if (!mainResult.success) {
      return {
        success: false,
        valid: false,
        overall: mainResult,
        byProtocol: {},
        byComponent: {}
      };
    }

    // Filter by protocol type
    const byProtocol = {
      wifi: filterWiFiValidation(mainResult),
      mqtt: filterMQTTValidation(mainResult),
      bluetooth: filterBluetoothValidation(mainResult),
      softwarePins: filterSoftwarePinValidation(mainResult)
    };

    // Group by component
    const byComponent = groupByComponent(mainResult);

    return {
      success: true,
      valid: mainResult.valid,
      overall: mainResult,
      byProtocol,
      byComponent
    };

  } catch (error) {
    console.error('❌ Error in comprehensive config validation:', error);
    return {
      success: false,
      valid: false,
      overall: handleApiError(error, 'Comprehensive configuration validation'),
      byProtocol: {},
      byComponent: {}
    };
  }
}

// ============================================================================
// EXPORT ALL FUNCTIONS
// ============================================================================

export default {
  // Main parser
  parseConfigValidation,
  
  // Validate all
  validateAllComponentConfigs,
  
  // Protocol filters
  filterWiFiValidation,
  filterMQTTValidation,
  filterBluetoothValidation,
  filterSoftwarePinValidation,
  
  // Component filters
  filterByComponent,
  groupByComponent,
  
  // Comprehensive
  parseComprehensiveConfigValidation
};